import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Pagination,
  SearchBox,
  Select,
  Spinner,
  apiClient,
  cn,
} from "@toss-ads/ui";
import { useUrlState } from "../lib/use-url-state";
import { useSearchField } from "../lib/use-search-field";

interface UserRow {
  id: string;
  email: string;
  role: "user" | "admin" | "developer";
  note: string | null;
  color: string | null;
  phone: string | null;
  smsOnLead: boolean;
  advertiser: { id: string; name: string } | null;
  emailVerifiedAt: string | null;
  lastSeenAt: string | null;
  disabled: boolean;
  createdAt: string;
}

interface UsersResponse {
  items: UserRow[];
  total: number;
  limit: number;
  offset: number;
}

const PER_PAGE = 20;

export function UsersListPage() {
  const navigate = useNavigate();
  // 필터·페이지는 URL 쿼리에 동기화 (뒤로가기/새로고침 복원).
  const searchField = useSearchField("q");
  const debouncedSearch = searchField.committed;
  const [roleRaw, setRoleFilter] = useUrlState("role", "");
  const roleFilter = roleRaw as "" | "user" | "admin";
  const [statusRaw, setStatusFilter] = useUrlState("status", "");
  const statusFilter = statusRaw as "" | "active" | "disabled";
  const [pageStr, setPageStr] = useUrlState("page", "1");
  const page = Number(pageStr) || 1;
  const setPage = React.useCallback(
    (n: number) => setPageStr(String(n)),
    [setPageStr],
  );

  // 필터가 실제로 바뀌면 1페이지로. 최초 마운트(새로고침)엔 URL 의 page 보존.
  const filterSig = [debouncedSearch, roleFilter, statusFilter].join("");
  const prevFilterSig = React.useRef(filterSig);
  React.useEffect(() => {
    if (prevFilterSig.current === filterSig) return;
    prevFilterSig.current = filterSig;
    setPage(1);
  }, [filterSig, setPage]);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<UsersResponse>({
      queryKey: [
        "admin",
        "users",
        { q: debouncedSearch, role: roleFilter, status: statusFilter, page },
      ],
      queryFn: () => {
        const params = new URLSearchParams({
          limit: String(PER_PAGE),
          offset: String((page - 1) * PER_PAGE),
        });
        if (debouncedSearch) params.set("q", debouncedSearch);
        if (roleFilter) params.set("role", roleFilter);
        if (statusFilter) params.set("status", statusFilter);
        return apiClient<UsersResponse>(`/api/admin/users?${params}`);
      },
    });

  const items = data?.items ?? [];
  const hasActiveFilter = Boolean(
    debouncedSearch || roleFilter || statusFilter,
  );

  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight">
            유저 관리
          </h1>
        </div>
        <Button size="md" onClick={() => navigate("/users/new")}>
          + 신규 유저 생성
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <SearchBox
          value={searchField.input}
          onChange={searchField.setInput}
          placeholder="이메일 검색"
        />
        <Select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
        >
          <option value="">권한 전체</option>
          <option value="user">user</option>
          <option value="admin">admin</option>
        </Select>
        <Select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as typeof statusFilter)
          }
        >
          <option value="">상태 전체</option>
          <option value="active">활성</option>
          <option value="disabled">비활성</option>
        </Select>

        {isFetching && !isLoading && (
          <span className="text-[11px] text-ink-subtle ml-2">갱신 중…</span>
        )}
      </div>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-surface-muted">
            <tr>
              <Th>이메일</Th>
              <Th>메모</Th>
              <Th width={160}>담당 광고주</Th>
              <Th width={150}>SMS 알림</Th>
              <Th width={90}>권한</Th>
              <Th width={90}>상태</Th>
              <Th width={150}>마지막 접속</Th>
              <Th width={120}>생성일</Th>
              <Th width={72}>수정</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={9} className="py-12 text-center">
                  <p className="text-[13px] text-danger mb-2">
                    {error instanceof ApiError
                      ? error.message
                      : "데이터를 불러오지 못했습니다."}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetch()}
                  >
                    다시 시도
                  </Button>
                </td>
              </tr>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="py-12 text-center text-[13px] text-ink-subtle"
                >
                  {hasActiveFilter
                    ? "필터 조건과 일치하는 유저가 없습니다."
                    : "등록된 유저가 없습니다."}
                </td>
              </tr>
            )}
            {items.map((u) => {
              const to = `/users/${u.id}`;
              return (
                <tr
                  key={u.id}
                  className="border-t border-border transition-colors hover:bg-surface-muted"
                >
                  <Td className="font-medium text-ink">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: u.color ?? "#9ca3af" }}
                        aria-hidden
                      />
                      {u.email}
                    </span>
                  </Td>
                  <Td className="text-ink-muted">
                    {u.note ? (
                      <span className="truncate inline-block max-w-[260px] align-bottom">
                        {u.note}
                      </span>
                    ) : (
                      <span className="text-ink-subtle">—</span>
                    )}
                  </Td>
                  <Td className="text-ink-muted">
                    {u.advertiser ? (
                      <span className="truncate inline-block max-w-[150px] align-bottom">
                        {u.advertiser.name}
                      </span>
                    ) : (
                      <span className="text-ink-subtle">미배정</span>
                    )}
                  </Td>
                  <Td>
                    {u.smsOnLead && u.phone ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Badge variant="success" dot>
                          수신
                        </Badge>
                        <span className="text-[11px] text-ink-muted tabular-nums">
                          {u.phone}
                        </span>
                      </span>
                    ) : (
                      <span className="text-ink-subtle text-[12px]">off</span>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      variant={
                        u.role === "developer"
                          ? "primary"
                          : u.role === "admin"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {u.role}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge variant={u.disabled ? "neutral" : "success"} dot>
                      {u.disabled ? "비활성" : "활성"}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">
                    {u.lastSeenAt ? formatDateTime(u.lastSeenAt) : "—"}
                  </Td>
                  <Td className="text-ink-muted">
                    {formatDate(u.createdAt)}
                  </Td>
                  <td className="px-2.5 py-3 align-middle">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(to)}
                    >
                      수정
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <Pagination
          total={data?.total ?? 0}
          perPage={PER_PAGE}
          current={page}
          onChange={setPage}
        />
      </div>
    </div>
  );
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={cn("px-2.5 py-3.5 align-middle", className)}>{children}</td>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}.${day} ${hh}:${mm}`;
}
